/**
 * useComparisonSlider - Hook for managing comparison slider state
 *
 * Handles pointer events for dragging the comparison slider handle.
 * Uses percentages (0-100) for position to work with any container size.
 *
 * Feature: 081-photo-versioning (T051)
 */

import { useState, useCallback, useRef, useEffect } from 'react';

export interface UseComparisonSliderOptions {
  /** Initial slider position (0-100, default 50) */
  initialPosition?: number;
  /** Orientation of the slider */
  orientation?: 'horizontal' | 'vertical';
  /** Callback when position changes */
  onPositionChange?: (position: number) => void;
}

export interface UseComparisonSliderReturn {
  /** Current slider position (0-100) */
  position: number;
  /** Set slider position directly */
  setPosition: (position: number) => void;
  /** Whether the slider is currently being dragged */
  isDragging: boolean;
  /** Ref to attach to the container element */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Handler for pointer down events (attach to slider handle) */
  handlePointerDown: (e: React.PointerEvent) => void;
  /** Keyboard handler for arrow key navigation */
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

/**
 * Hook for managing comparison slider interaction
 */
export function useComparisonSlider(
  options: UseComparisonSliderOptions = {}
): UseComparisonSliderReturn {
  const {
    initialPosition = 50,
    orientation = 'horizontal',
    onPositionChange,
  } = options;

  const [position, setPositionState] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Clamp position to 0-100 range
  const clampPosition = useCallback((pos: number): number => {
    return Math.max(0, Math.min(100, pos));
  }, []);

  // Set position with clamping and callback
  const setPosition = useCallback((newPosition: number) => {
    const clamped = clampPosition(newPosition);
    setPositionState(clamped);
    onPositionChange?.(clamped);
  }, [clampPosition, onPositionChange]);

  // Calculate position from pointer event
  const calculatePosition = useCallback((clientX: number, clientY: number): number => {
    if (!containerRef.current) return position;

    const rect = containerRef.current.getBoundingClientRect();

    if (orientation === 'horizontal') {
      const x = clientX - rect.left;
      return (x / rect.width) * 100;
    } else {
      const y = clientY - rect.top;
      return (y / rect.height) * 100;
    }
  }, [orientation, position]);

  // Handle pointer move during drag
  const handlePointerMove = useCallback((e: PointerEvent) => {
    if (!isDragging) return;

    const newPosition = calculatePosition(e.clientX, e.clientY);
    setPosition(newPosition);
  }, [isDragging, calculatePosition, setPosition]);

  // Handle pointer up to end drag
  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Set up and clean up global event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('pointermove', handlePointerMove);
      window.addEventListener('pointerup', handlePointerUp);

      return () => {
        window.removeEventListener('pointermove', handlePointerMove);
        window.removeEventListener('pointerup', handlePointerUp);
      };
    }
  }, [isDragging, handlePointerMove, handlePointerUp]);

  // Handle pointer down on slider handle
  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);

    // Calculate initial position from click
    const newPosition = calculatePosition(e.clientX, e.clientY);
    setPosition(newPosition);
  }, [calculatePosition, setPosition]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    const step = e.shiftKey ? 10 : 1; // Larger step with shift key

    switch (e.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        setPosition(position - step);
        break;
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        setPosition(position + step);
        break;
      case 'Home':
        e.preventDefault();
        setPosition(0);
        break;
      case 'End':
        e.preventDefault();
        setPosition(100);
        break;
    }
  }, [position, setPosition]);

  return {
    position,
    setPosition,
    isDragging,
    containerRef,
    handlePointerDown,
    handleKeyDown,
  };
}
